import React, { useMemo, useState } from 'react';
import { SearchIcon, ChevronRightIcon, InboxIcon } from 'lucide-react';
import type { UploadStatus } from '../../lib/types';
import { UPLOAD_HISTORY } from '../../lib/mockData';
import { StatusBadge } from '../ui/StatusBadge';
import { cn, formatNumber } from '../../lib/utils';
const STATUS_FILTERS: {
  value: UploadStatus | 'all';
  label: string;
}[] = [{
  value: 'all',
  label: 'All'
}, {
  value: 'completed',
  label: 'Completed'
}, {
  value: 'completed-with-warnings',
  label: 'With warnings'
}, {
  value: 'partially-completed',
  label: 'Partial'
}, {
  value: 'failed',
  label: 'Failed'
}];
export function HistoryView() {
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState<UploadStatus | 'all'>('all');
  const rows = useMemo(() => {
    return UPLOAD_HISTORY.filter(u => {
      const matchQuery = u.fileName.toLowerCase().includes(query.toLowerCase()) || u.uploadedBy.toLowerCase().includes(query.toLowerCase()) || u.vendor.toLowerCase().includes(query.toLowerCase());
      const matchFilter = filter === 'all' || u.status === filter;
      return matchQuery && matchFilter;
    });
  }, [query, filter]);
  return <div className="space-y-5" data-id="element-313" data-mp="2ggi1jnop5ww8">
      {/* Filters */}
      <div className="flex flex-wrap items-center justify-between gap-3" data-id="element-314" data-mp="2lf2z1fcke2gz">
        <div className="relative w-full max-w-xs" data-id="element-315" data-mp="27gbou40tp0y4">
          <SearchIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" data-id="element-316" data-mp="27eii6oq7mgq4" />
          <input value={query} onChange={e => setQuery(e.target.value)} placeholder="Search by file, user or vendor…" className="w-full rounded-lg border border-gray-300 bg-white py-2.5 pl-9 pr-3 text-sm text-gray-900 placeholder:text-gray-400 focus:border-salesforce-500 focus:outline-none focus:ring-2 focus:ring-salesforce-500/40" data-id="element-317" data-mp="2bfkjrc7ln4v9" />
        </div>
        <div className="flex flex-wrap items-center gap-1.5" data-id="element-318" data-mp="n9jet99dxs8">
          {STATUS_FILTERS.map(f => <button key={f.value} type="button" onClick={() => setFilter(f.value)} className={cn('rounded-lg px-3 py-1.5 text-xs font-medium transition-colors', filter === f.value ? 'bg-salesforce-500 text-white' : 'bg-white text-gray-600 border border-gray-300 hover:bg-gray-50')} data-id="element-319" data-mp="3vbhzjzsyhnu3">
              {f.label}
            </button>)}
        </div>
      </div>

      {/* Table */}
      <div className="rounded-2xl border border-gray-200 bg-white shadow-card" data-id="element-320" data-mp="88d89la9owdz">
        <div className="thin-scroll overflow-x-auto" data-id="element-321" data-mp="3aleelq2b075">
          <table className="w-full min-w-[1100px] text-left text-sm" data-id="element-322" data-mp="39s5be3mrylu">
            <thead data-id="element-323" data-mp="10qhb1ivpbl0i">
              <tr className="border-b border-gray-100 bg-gray-50 text-xs uppercase tracking-wide text-gray-500" data-id="element-324" data-mp="10qqu0mibwrwp">
                <th className="px-5 py-3 font-medium" data-id="element-325" data-mp="vwmma118ofuk">Date</th>
                <th className="px-5 py-3 font-medium" data-id="element-326" data-mp="148omqzpxvfpp">File</th>
                <th className="px-5 py-3 font-medium" data-id="element-327" data-mp="188oygclnlct2">Uploaded by</th>
                <th className="px-5 py-3 font-medium" data-id="element-328" data-mp="18b1geprevn64">Vendor</th>
                <th className="px-5 py-3 font-medium text-right" data-id="element-329" data-mp="1d7a10iodo8ko">Rows</th>
                <th className="px-5 py-3 font-medium" data-id="element-330" data-mp="1i7o19ejqme85">Salesforce</th>
                <th className="px-5 py-3 font-medium" data-id="element-331" data-mp="1i4s2xp3z9nj6">Bonzo</th>
                <th className="px-5 py-3 font-medium" data-id="element-332" data-mp="1n5pu2r6ytrwt">Five9</th>
                <th className="px-5 py-3 font-medium" data-id="element-333" data-mp="1r7azjrh673wz">Status</th>
                <th className="px-5 py-3" data-id="element-334" data-mp="1r5gemd5wfbsw" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50" data-id="element-335" data-mp="1vzb6w4zkkz3l">
              {rows.map(u => <tr key={u.id} className="cursor-pointer text-gray-700 hover:bg-gray-50/70" data-id="element-336" data-mp="20x4f13kfo36i">
                  <td className="whitespace-nowrap px-5 py-3 text-xs text-gray-500" data-id="element-337" data-mp="2avk470zwh2dd">
                    {u.date}
                  </td>
                  <td className="px-5 py-3 font-medium text-gray-900" data-id="element-338" data-mp="37lw0g4fvdsfr">
                    {u.fileName}
                  </td>
                  <td className="whitespace-nowrap px-5 py-3" data-id="element-339" data-mp="32lzwg576a107">
                    {u.uploadedBy}
                  </td>
                  <td className="px-5 py-3" data-id="element-340" data-mp="3cdx8fe608wzz">{u.vendor}</td>
                  <td className="px-5 py-3 text-right tabular-nums" data-id="element-341" data-mp="3ceff74qamtm5">
                    {formatNumber(u.totalRows)}
                  </td>
                  <td className="px-5 py-3" data-id="element-342" data-mp="2jp85rapawjm4">
                    <StatusBadge status={u.salesforceResult} size="sm" data-id="element-343" data-mp="2jqjjo3cgn7qy" />
                  </td>
                  <td className="px-5 py-3" data-id="element-344" data-mp="2xq2clowpacio">
                    <div className="flex flex-col gap-1" data-id="element-345" data-mp="2xmodcpjc2jfg">
                      <StatusBadge status={u.bonzoResult} size="sm" data-id="element-346" data-mp="2tmmm7ab858ef" />
                      <span className="truncate text-[11px] text-gray-400" data-id="element-347" data-mp="cy8y5654sdvi">
                        {u.bonzoCampaign}
                      </span>
                    </div>
                  </td>
                  <td className="px-5 py-3" data-id="element-348" data-mp="gg6gqqj11las">
                    <div className="flex flex-col gap-1" data-id="element-349" data-mp="gf4v5s4dxget">
                      <StatusBadge status={u.five9Result} size="sm" data-id="element-350" data-mp="lgc2jq2m8dky" />
                      <span className="truncate text-[11px] text-gray-400" data-id="element-351" data-mp="ldg5e3yldsxk">
                        {u.five9List}
                      </span>
                    </div>
                  </td>
                  <td className="px-5 py-3" data-id="element-352" data-mp="bjpjn6tunwlk">
                    <StatusBadge status={u.status} size="sm" data-id="element-353" data-mp="z6xmli3t2tkf" />
                  </td>
                  <td className="px-5 py-3 text-right" data-id="element-354" data-mp="146trdjkbup11">
                    <ChevronRightIcon className="h-4 w-4 text-gray-400" data-id="element-355" data-mp="148d7na5zh02t" />
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>
        {rows.length === 0 && <div className="flex flex-col items-center justify-center gap-2 py-16 text-center" data-id="element-356" data-mp="ue4huu8c7l8c">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-gray-100" data-id="element-357" data-mp="1i48l30hm5iyt">
              <InboxIcon className="h-6 w-6 text-gray-400" data-id="element-358" data-mp="1mymkrf3b6c9a" />
            </div>
            <p className="text-sm font-medium text-gray-900" data-id="element-359" data-mp="1wrz6k1uf631p">
              No uploads found
            </p>
            <p className="text-sm text-gray-500" data-id="element-360" data-mp="1rrkwke99cj11">
              Try adjusting your search or filters.
            </p>
          </div>}
      </div>
    </div>;
}